/**
 * web/src/hooks/useUiMemory.ts — the React side of lib/uiState.ts
 * (UX item 5, findings H5/H6).
 *
 *   useRemembered(name, initial)   state that survives a remount
 *   useScrollMemory(ref, name)     scroll position that survives a remount
 *
 * Both are session-scoped; see lib/uiState.ts for why sessionStorage and why
 * nothing read here is a source of truth.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { forget, recall, remember, rememberScroll, recallScroll } from '../lib/uiState.ts';

/**
 * useState, backed by the session bag. The remembered value is the initial
 * state on mount — set it as a plain write and a remount picks it back up.
 */
export function useRemembered<T>(name: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => recall<T>(name) ?? initial);
  const set = (next: T) => {
    remember(name, next);
    setValue(next);
  };
  return [value, set];
}

/**
 * Restore a container's scroll position on mount and keep the record fresh
 * while the reader scrolls.
 *
 * The restore is retried across animation frames because a panel mounts
 * empty: its content arrives one render later, and a scrollTop written before
 * the content is tall enough is clamped straight back to 0. While a restore is
 * in flight the element carries `data-ui-restore`, so a surface with its own
 * auto-scroll (the Thread's "new message pulls you down") can stand aside.
 *
 * Writes are suppressed for a short settle window after the effect runs: React
 * commits the NEW panel before the effect switches keys, so a clamp-scroll
 * fired by the old content shrinking would otherwise be recorded against the
 * tab that just came up.
 */
export function useScrollMemory(ref: RefObject<HTMLElement | null>, name: string): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const record = recallScroll(name);
    let stopRestore = false;
    let tries = 0;

    if (record && !record.atBottom) {
      el.dataset.uiRestore = '1';
      const apply = () => {
        if (stopRestore) return;
        el.scrollTop = record.top;
        tries += 1;
        // Keep trying while the content is still too short to reach the
        // remembered offset (~1.5s of frames), then give up quietly.
        if (Math.abs(el.scrollTop - record.top) > 2 && tries < 90) {
          requestAnimationFrame(apply);
        } else {
          delete el.dataset.uiRestore;
        }
      };
      requestAnimationFrame(apply);
    }

    const settleUntil = Date.now() + 400;
    let raf = 0;
    const save = () => {
      if (Date.now() < settleUntil) return;
      rememberScroll(name, {
        top: el.scrollTop,
        atBottom: el.scrollHeight - el.scrollTop - el.clientHeight < 8,
      });
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        save();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      stopRestore = true;
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
      delete el.dataset.uiRestore;
    };
  }, [ref, name]);
}

/** Send a draft and forget it — exported so a screen can clear deliberately. */
export { forget as forgetUiValue };
